import React, { useState, useEffect, useMemo } from 'react'
import { Button } from 'react-bootstrap'
import { string, bool, arrayOf, func, shape, oneOfType, number } from 'prop-types'

import { SearchInput } from '../../search-input'
import Badge from '../../badge/badge'
import DraggableListItem from './draggable-list-item'
import useList from './use-list'
import s from './list.module.scss'

const List = props => {
  const {
    canFilter,
    filterPlaceholder,
    disabled,
    options,
    title,
    value,
    onChange,
    handleDropItems,
    extraOptions,
    getSelectedOptionName,
    filter,
    setFilter,
    displayParentGroups = false,
    unGroupedTitle,
    handleAddGroupItems,
    handleRemoveGroupItems,
  } = props

  const { computedOptions, handleOptionChange, checkIsSelected, dropRef, groupOptionsByParent } = useList({
    canFilter,
    options,
    value,
    onChange,
    handleDropItems,
    filter,
    setFilter,
  })

  // Group options by their parent labels if displayParentGroups is true
  const { groupedOptions, parentLabels } = useMemo(() => {
    if (displayParentGroups) {
      const result = groupOptionsByParent(computedOptions)
      return { groupedOptions: result.grouped, parentLabels: result.labels }
    } else {
      return { groupedOptions: null, parentLabels: null }
    }
  }, [displayParentGroups, computedOptions, groupOptionsByParent])

  const showGroups = displayParentGroups && groupedOptions && Object.keys(groupedOptions).length > 0

  const [expandedGroups, setExpandedGroups] = useState({})

  // Helper to determine if a group's children match the filter
  const groupHasMatch = (items, search) => {
    return items.some(opt => {
      const label = String(opt.label).toLowerCase()
      const value = String(opt.value).toLowerCase()
      return label.includes(search) || value.includes(search)
    })
  }

  // When the filter changes, expand only the groups whose children match the filter
  useEffect(() => {
    if (!showGroups) return
    const groupKeys = Object.keys(groupedOptions || {})
    const search = (filter || '').trim().toLowerCase()
    setExpandedGroups(() => {
      const updated = {}
      groupKeys.forEach(groupId => {
        // If filtering, expand only groups with matches. Otherwise, expand all
        if (canFilter && search) {
          updated[groupId] = groupHasMatch(groupedOptions[groupId], search)
        } else {
          updated[groupId] = true
        }
      })
      return updated
    })
  }, [showGroups, groupedOptions, filter, canFilter])

  const toggleGroup = group => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }))
  }

  // Render group action button
  const renderGroupActionButton = (items) => {
    const groupValues = items.map(option => option.value)
    const isAdd = typeof handleAddGroupItems === 'function'
    const isRemove = typeof handleRemoveGroupItems === 'function'
    return (
      <Button
        title={isAdd ? 'Add' : 'Remove' }
        variant="outline-secondary"
        size="sm"
        className={s['update-group-button']}
        onClick={e => {
          if (e.stopPropagation) e.stopPropagation()
          if (isAdd) {
            handleAddGroupItems(groupValues)
          } else if (isRemove) {
            handleRemoveGroupItems(groupValues)
          }
        }}
      >
        {isRemove ? (
          <i className="bi bi-arrow-left"></i>
        ) : (
          <i className="bi bi-arrow-right"></i>
        )}
      </Button>
    )
  }

  // Render extra options
  const renderExtraOptions = (optionValue) => (
    typeof extraOptions === 'function' ? extraOptions(optionValue) : null
  )

  return (
    <div className={s['container']}>
      {canFilter && (
        <SearchInput
          placeholder={filterPlaceholder}
          value={filter}
          onChange={setFilter}
          inputClassName={s['filter']}
          disabled={disabled}
        />
      )}
      <div className={s['list-wrapper']} ref={dropRef}>
        <div className={s['list-title']}>{title}</div>
        <div className={s['list-scroll-area']}>
          <ul className={s['list']} role="listbox" aria-orientation="vertical">
            {showGroups
              ? Object.entries(groupedOptions).map(([groupId, items]) => (
                  <React.Fragment key={groupId}>
                    <li
                      className={s['group-header']}
                      onClick={() => toggleGroup(groupId)}
                    >
                      <span className={s['group-header-flex-span']}>
                        <svg
                          height="20"
                          width="20"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                          focusable="false"
                          className={s['group-header-arrow']}
                          style={{
                            transform: expandedGroups[groupId] ? 'none' : 'rotate(-90deg)'
                          }}
                        >
                          <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
                        </svg>
                        <Badge key={parentLabels[groupId]} status={groupId !== 'ungrouped' ? parentLabels[groupId] : unGroupedTitle ?? 'No Group'} color="#065796" />
                      </span>
                      {renderGroupActionButton(items)}
                    </li>
                    {expandedGroups[groupId] &&
                      items.map(option => (
                        <DraggableListItem
                          key={option.value}
                          handleClick={() => handleOptionChange(option.value)}
                          label={getSelectedOptionName(option)}
                          item={value}
                          options={options}
                          value={option.value}
                          disabled={disabled}
                          isSelected={checkIsSelected(option.value)}
                          isChildItem
                          extraContent={renderExtraOptions(option.value)}
                        />
                      ))}
                  </React.Fragment>
                ))
              : computedOptions.map(option => (
                  <DraggableListItem
                    key={option.value}
                    handleClick={() => handleOptionChange(option.value)}
                    label={getSelectedOptionName(option)}
                    item={value}
                    options={options}
                    value={option.value}
                    disabled={disabled}
                    isSelected={checkIsSelected(option.value)}
                    extraContent={renderExtraOptions(option.value)}
                  />
                ))}
          </ul>
        </div>
      </div>
    </div>
  )
}


List.defaultProps = {
  getSelectedOptionName: ({ label }) => label,
}


List.propTypes = {
  canFilter: bool,
  disabled: bool,
  options: arrayOf(
    shape({
      label: oneOfType([string, number]),
      value: oneOfType([string, number]),
      parents: arrayOf(
        shape({
          label: oneOfType([string, number]),
          value: oneOfType([string, number]),
        })
      ),
    })
  ).isRequired,
  title: oneOfType([string, number]),
  value: arrayOf(oneOfType([string, number])),
  onChange: func,
  handleDropItems: func,
  getSelectedOptionName: func,
  handleAddGroupItems: func,
  handleRemoveGroupItems: func,
  displayParentGroups: bool,
  unGroupedTitle: string,
}

export default List
